import { createWithEqualityFn } from 'zustand/traditional';
import { persist, createJSONStorage } from 'zustand/middleware';

export type WorksheetState = {
  open: boolean;
  questions: Array<{
    title: string;
    slug: string;
  }>;
  allSelected: boolean;
  options: {
    area: boolean;
    subtopic: boolean;
    difficulty: boolean;
    series: boolean;
    paper: boolean;
    questionMsQR: boolean;
    questionWrittenMsQR: boolean;
    writtenMsQR: boolean;
    markSchemePDF: boolean;
  };
  cover: {
    title: string;
    subtitle: string;
    comments: string;
    timeAllowed: string;
    totalMarks: string;
    startDate: string;
  };
  loading: boolean;
  actions: {
    toggleOpen: () => void;
    setOpen: (open: boolean) => void;
    setAllSelected: (value: boolean) => void;
    addQuestion: (question: { title: string; slug: string }) => void;
    removeQuestion: (id: string) => void;
    toggleOption: (option: keyof typeof defaults.options) => void;
    setCoverText: (key: keyof typeof defaults.cover, value: string) => void;
    setLoading: (loading: boolean) => void;
    reset: () => void;
  };
};

export const defaults: Omit<WorksheetState, 'actions'> = {
  open: true,
  questions: [],
  allSelected: false,
  options: {
    area: false,
    subtopic: false,
    difficulty: false,
    series: false,
    paper: false,
    questionMsQR: true,
    questionWrittenMsQR: true,
    writtenMsQR: true,
    markSchemePDF: true,
  },
  cover: {
    title: 'Worksheet Created By My Maths Cloud',
    subtitle: 'mymathscloud.com',
    comments: '',
    timeAllowed: '',
    totalMarks: '',
    startDate: '',
  },
  loading: false,
};

const useWorksheetStore = createWithEqualityFn<WorksheetState>()(
  persist(
    (set) => ({
      ...defaults,

      actions: {
        toggleOpen() {
          set((state) => ({ ...state, open: !state.open }));
        },
        setOpen(open) {
          set((state) => ({ ...state, open }));
        },
        setAllSelected(value: boolean) {
          set((state) => ({ ...state, allSelected: value }));
        },
        addQuestion(question) {
          set((state) => {
            return {
              ...state,
              questions: [...state.questions, question],
            };
          });
        },
        removeQuestion(slug) {
          set((state) => {
            return {
              ...state,
              questions: state.questions.filter((question) => question.slug !== slug),
            };
          });
        },
        toggleOption(option: keyof typeof defaults.options) {
          set((state) => {
            return {
              ...state,
              options: {
                ...state.options,
                [option]: !state.options[option],
              },
            };
          });
        },
        setLoading(loading) {
          set((state) => ({ ...state, loading }));
        },
        setCoverText(key, value) {
          set((state) => {
            return {
              ...state,
              cover: {
                ...state.cover,
                [key]: value,
              },
            };
          });
        },
        reset() {
          set((state) => ({ ...state, ...defaults }), true);
        },
      },
    }),
    {
      name: 'worksheet',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !['actions'].includes(key)),
        ) as typeof state,
    },
  ),
  Object.is,
);

export const useIsWorksheetOpen = () => useWorksheetStore((state) => state.open);
export const useQuestions = () => useWorksheetStore((state) => state.questions);
export const useHasQuestions = () => useWorksheetStore((state) => state.questions.length > 0);

export const useOptions = () => useWorksheetStore((state) => state.options);
export const useOption = (option: keyof typeof defaults.options) =>
  useWorksheetStore((state) => state.options[option]);

export const useQuestionIsInWorksheet = (slug: string) =>
  useWorksheetStore((state) => {
    return state.questions.some((question) => question.slug === slug);
  });

export const useIsCreatingWorksheet = () => useWorksheetStore((state) => state.loading);

export const useCover = () => useWorksheetStore((state) => state.cover);
export const useCoverFields = (input: keyof typeof defaults.cover) =>
  useWorksheetStore((state) => state.cover[input]);
export const useHasEnteredCoverDetails = () =>
  useWorksheetStore((state) => {
    return Object.values(state.cover).some((value) => value.length > 0);
  });

export const useAllSelected = () => useWorksheetStore((state) => state.allSelected);

export const worksheetActions = useWorksheetStore.getState().actions;
